import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Breadcrumbs from "../common/Breadcrumbs";
import { useLocation } from "react-router-dom";
import Aos from "aos";
import { AppDispatch, RootState } from "../../core/data/redux/store";
import { useDispatch, useSelector } from "react-redux";
import { fetchVehicleById } from "../../core/data/redux/action";
import ImageWithURL from "../../core/data/img/ImageWithURL";
import ContactOwner from "../authentication/contactowner";

const listingDetails = () => {
  const dispatch: AppDispatch = useDispatch();
  const [vehicleDetail, setLocalVehicle] = useState<any>([]);
  const vehicle = useSelector((state: RootState) => state.vehicle);
  const location = useLocation();

  const getID = () => {
    const params = new URLSearchParams(location.search);
    return params.get("id");
  };
  const id = getID();

  const [minLoadTimeElapsed, setMinLoadTimeElapsed] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setMinLoadTimeElapsed(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [vehicleAvailability, setVehicleAvailability] = useState<boolean | null>(null);

  useEffect(() => {
    if (startDate === "" || endDate === "") setVehicleAvailability(null);
  }, [startDate, endDate]);

  const checkAvailability = async (vehicle: any, start: string, end: string) => {
    const apiUrl = `https://admin.eleego.ch/api/v1/vehicles-availability`;
    const params = new URLSearchParams({ start_date: start, end_date: end, vehicle_id: vehicle.id });
    try {
      const response = await fetch(`${apiUrl}?${params.toString()}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const responseData = await response.json();
      setVehicleAvailability(responseData.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => { dispatch(fetchVehicleById(id || 1)); }, [dispatch, location]);
  useEffect(() => { setLocalVehicle(vehicle.data); }, [vehicle]);
  useEffect(() => { Aos.init({ duration: 1000, once: true }); }, []);

  // ── Loading screen ───────────────────────────────────────
  if (!vehicleDetail || !vehicleDetail.name || !minLoadTimeElapsed) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", background: "#0A0A0A", gap: "20px" }}>
        <div style={{ position: "relative", width: "72px", height: "72px" }}>
          <svg style={{ animation: "spin 2s linear infinite", width: "100%", height: "100%" }} viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
            <circle cx="60" cy="60" r="54" style={{ stroke: "rgba(200,164,93,0.15)", strokeWidth: 2, fill: "none" }} />
            <circle cx="60" cy="60" r="54" style={{ stroke: "var(--luxury-gold)", strokeWidth: 2, fill: "none", strokeDasharray: "340", strokeDashoffset: "280" }} />
          </svg>
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--luxury-gold)" }}>
            <i className="fa-solid fa-car" style={{ fontSize: "22px" }} />
          </div>
        </div>
        <p className="lux-eyebrow" style={{ letterSpacing: "4px", margin: 0 }}>PREPARING SHOWROOM</p>
      </div>
    );
  }

  // ── Page ─────────────────────────────────────────────────
  return (
    <>
      <Helmet>
        <title>{`${vehicleDetail.name} | Eleego Luxury Rentals`}</title>
        <meta name="description" content={`Rent the premium ${vehicleDetail.name} in Switzerland. Learn details, availability, and rental terms.`} />
        <meta property="og:title" content={`${vehicleDetail.name} - Eleego`} />
        <meta property="og:description" content={`Rent the premium ${vehicleDetail.name} in Switzerland. Learn details, availability, and rental terms.`} />
        <meta property="og:image" content={vehicleDetail.image_url} />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Car",
              "name": "${vehicleDetail.name}",
              "image": "${vehicleDetail.image_url}",
              "description": "Premium luxury ${vehicleDetail.category || 'rental'} vehicle",
              "brand": {
                "@type": "Brand",
                "name": "${vehicleDetail.brand_name || 'Eleego'}"
              }
              ${vehicleDetail.per_day_amount ? `,
              "offers": {
                "@type": "Offer",
                "priceCurrency": "CHF",
                "price": "${vehicleDetail.per_day_amount}"
              }` : ''}
            }
          `}
        </script>
      </Helmet>
    <div className="main-wrapper lux-detail-page">
      <Breadcrumbs title={vehicleDetail.name} subtitle={vehicleDetail.name} maintitle="" />

      {/* ── Hero title band ── */}
      <div className="lux-detail-hero" data-aos="fade-down" data-aos-duration="800">
        <div className="container">
          <p className="lux-eyebrow mb-2">{vehicleDetail.category || "PREMIUM VEHICLE"}</p>
          <h1 className="lux-detail-title">{vehicleDetail.name}</h1>
          {vehicleDetail.brand_name && (
            <p className="lux-detail-brand">{vehicleDetail.brand_name}</p>
          )}
        </div>
      </div>

      {/* ── Main content grid ── */}
      <section className="lux-detail-content">
        <div className="container">
          <div className="row g-4">

            {/* ── LEFT: Image + Description ── */}
            <div className="col-lg-8" data-aos="fade-up" data-aos-duration="900">

              {/* Vehicle image — full-bleed cinematic */}
              <div className="lux-detail-image-wrap">
                <ImageWithURL
                  src={vehicleDetail.image_url}
                  alt={vehicleDetail.name}
                  className="lux-detail-image"
                />
                <div className="lux-detail-image-overlay" />
                {vehicleDetail.category === "Rental" && vehicleDetail.per_day_amount && (
                  <div className="lux-price-badge">
                    <span className="lux-price-badge__label">From</span>
                    <span className="lux-price-badge__amount">
                      {vehicleDetail.per_day_amount} <em>CHF</em>
                    </span>
                    <span className="lux-price-badge__unit">/ Day</span>
                  </div>
                )}
              </div>

              {/* Description card */}
              <div className="lux-detail-desc-card" data-aos="fade-up" data-aos-delay="150">
                <div className="lux-detail-section-label">
                  <span className="lux-eyebrow">ABOUT THIS VEHICLE</span>
                  <div className="lux-detail-divider" />
                </div>
                <div className="lux-detail-desc-body">
                  {vehicleDetail.description ? (
                    vehicleDetail.description
                      .split("\n")
                      .filter((p: string) => p.trim())
                      .map((para: string, idx: number) => (
                        <p key={idx}>{para}</p>
                      ))
                  ) : (
                    <p className="lux-detail-no-desc">No description provided for this vehicle.</p>
                  )}
                </div>
              </div>
            </div>

            {/* ── RIGHT: Booking card ── */}
            <div className="col-lg-4" data-aos="fade-left" data-aos-duration="900" data-aos-delay="100">
              <div className="lux-booking-card">
                {/* Card header */}
                <div className="lux-booking-card__header">
                  <i className="fa-regular fa-calendar-check lux-booking-card__icon" />
                  <div>
                    <p className="lux-eyebrow mb-1">RESERVE NOW</p>
                    <h3 className="lux-booking-card__title">Check Availability</h3>
                  </div>
                </div>

                <div className="lux-booking-card__divider" />

                {/* Date inputs */}
                <div className="lux-booking-card__body">
                  <div className="lux-field-group">
                    <label htmlFor="lux-pickup" className="lux-field-label">
                      <i className="fa-regular fa-clock" /> Pickup Date & Time
                    </label>
                    <input
                      id="lux-pickup"
                      type="datetime-local"
                      className="lux-field-input"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>

                  <div className="lux-field-group">
                    <label htmlFor="lux-return" className="lux-field-label">
                      <i className="fa-regular fa-clock" /> Return Date & Time
                    </label>
                    <input
                      id="lux-return"
                      type="datetime-local"
                      className="lux-field-input"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>

                  {/* Hint when incomplete */}
                  {(!startDate || !endDate) && (
                    <p className="lux-booking-hint">
                      <i className="fa-solid fa-circle-info" /> Select both dates to continue
                    </p>
                  )}

                  <button
                    className="lux-btn-gold w-100 lux-booking-btn"
                    type="button"
                    data-bs-toggle="modal"
                    data-bs-target="#pages_edit"
                    onClick={() => checkAvailability(vehicleDetail, startDate, endDate)}
                    disabled={!startDate || !endDate}
                  >
                    <i className="fa-solid fa-magnifying-glass me-2" />
                    Check Availability
                  </button>
                </div>

                {/* Footer note */}
                <p className="lux-booking-card__note">
                  <i className="fa-solid fa-shield-halved me-1" />
                  Free cancellation within 24 hours
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Availability Modal ── */}
      <div className="modal custom-modal lux-modal fade" id="pages_edit" role="dialog">
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div className="modal-content lux-modal__content">
            <div className="modal-header lux-modal__header">
              <div>
                <p className="lux-eyebrow mb-1">AVAILABILITY RESULT</p>
                <h4 className="lux-modal__title">Booking Status</h4>
              </div>
              <button type="button" className="lux-modal__close" data-bs-dismiss="modal" aria-label="Close">
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body lux-modal__body">
              {vehicleAvailability === true ? (
                <>
                  <div className="lux-status lux-status--available">
                    <i className="fa-regular fa-circle-check lux-status__icon" />
                    <div>
                      <strong>{vehicleDetail.name}</strong>
                      <p>is available for your selected dates.</p>
                    </div>
                  </div>
                  <ContactOwner
                    vehicleData={vehicleDetail}
                    startDate={startDate}
                    endDate={endDate}
                  />
                </>
              ) : (
                <div className="lux-status lux-status--unavailable">
                  <i className="fa-regular fa-circle-xmark lux-status__icon" />
                  <div>
                    <strong>{vehicleDetail.name}</strong>
                    <p>is not available for the selected dates. Please try different dates.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default listingDetails;
